import Cookies from 'react-cookies';
import {
  API_CONFIG,
  AWIN_ADVERTISER_ID,
  CURRENCY_CONFIG,
} from '../brands/default/config';
import { AWINCHANNEL, COOKIES } from '../brands/default/constants';
import { AFFILIATE_EMAIL } from '../brands/default/services';
import axios from '../components/_generic/axiosSplunk/axiosSplunk';
import commonCookiesOptions from './commonCookiesOptions';
import { fixPrecision } from './currency';
import { getAppPlatform } from './device';
import ErrorTrackingService from './errorTrackingService';

export const getAffiliateData = async (affiliateName) => {
  if (!affiliateName) return null;
  const url = `${AFFILIATE_EMAIL.replace('##AFFILIATE_NAME##', affiliateName)}`;
  try {
    const { data } = await axios({
      cache: false,
      headers: {
        'app-platform': getAppPlatform(),
        brand: API_CONFIG.BRAND_HEADER,
        'country-code': process.env.NEXT_PUBLIC_COUNTRY_CODE || 'gb',
        webapp: true,
        ...(API_CONFIG.SUB_BRAND && { 'sub-brand': API_CONFIG.SUB_BRAND }),
      },
      method: 'GET',
      url,
    });

    return data;
  } catch (error) {
    ErrorTrackingService.logError(error);
    throw new Error(error);
  }
};

// Affiliates that get extra cash for bringing in new customers.
// This is a hard coded list as those values are unlikely to change for many years

const NEW_PURCHASES_AFFILIATES = ['54760', '57697'];

export const getAffiliateId = () => {
  const userSource = Cookies.load(COOKIES.userSource) || '';
  const brand = process.env.NEXT_PUBLIC_BRAND;

  return userSource.replace(`${brand.toLowerCase()}_affiliate_`, '');
};

export const isNewClient = () => {
  const userProfile = Cookies.load(COOKIES.userProfile) || '';

  if (Cookies.load(COOKIES.affiliateUser)) return false;
  if (userProfile.previousPurchase) return false;

  const expires = new Date();
  expires.setDate(expires.getDate() + 30);

  Cookies.save(COOKIES.affiliateUser, true, {
    ...commonCookiesOptions,
    expires,
  });

  return true;
};

export const isNewPurchasesAffiliate = () => {
  const affiliateId = getAffiliateId();

  return NEW_PURCHASES_AFFILIATES.some((id) => affiliateId === id);
};

// set the promocode given list of new products, there is only one promocode per order but it may be on just one product
const setPromocode = (newProducts) => {
  if (newProducts && newProducts.length >= 1) {
    return newProducts.forEach((product) => {
      if (product.coupon) return product.coupon;

      return '';
    });
  } else return '';
};

// Tracks data for AWIN (affiliate marketing tool)
export const trackPurchaseAffiliate = ({
  deals,
  orderId,
  products,
  isFirstPurchase = false,
}) => {
  if (!deals || !orderId || !products || !window.AWIN) return null;

  try {
    const getProductCategoryName = (dealId) => {
      const productDeal = deals.find((deal) => deal.id === dealId);
      // category is not available for express buy

      return productDeal?.category?.name ? productDeal.category.name : '';
    };

    const getProductCommissionGroup = (categoryName) => {
      if (isFirstPurchase) {
        return 'NEW';
      }

      return categoryName
        ? categoryName.split(' ').join('-').replace(/&/g, 'and').toUpperCase()
        : '';
    };

    const getParts = (dealInfo) => {
      return Object.keys(dealInfo)
        .map(
          (dealId) =>
            `${dealInfo[dealId].commissionGroup}:${fixPrecision(
              dealInfo[dealId].totalCost,
            )}`,
        )
        .reduce((previous, current) => `${previous}|${current}`);
    };

    // Create a function to generate sProductData
    const generateSProductData = () => {
      const productLines = [];

      products.forEach((product) => {
        const categoryName = getProductCategoryName(product.dealId);
        const commissionGroup = getProductCommissionGroup(categoryName);

        // Construct each product line and add it to productLines array

        const productLine = [
          'AW:P',
          AWIN_ADVERTISER_ID,
          orderId,
          product.id,
          product.title,
          product.price,
          product.quantity,
          '',
          commissionGroup,
          categoryName,
        ].join('|');

        productLines.push(productLine);
      });

      return productLines.join('\r\n'); // Join product lines with line breaks
    };

    const sProductData = generateSProductData();

    // Create and append the awProductsTextArea
    const awProductsForm = document.createElement('form');
    awProductsForm.name = 'aw_basket_form';
    awProductsForm.setAttribute('style', 'display: none;');
    document.querySelectorAll('body')[0].appendChild(awProductsForm);

    const awProductsTextArea = document.createElement('textarea');
    awProductsTextArea.id = 'aw_basket';
    awProductsTextArea.wrap = 'physical';

    awProductsTextArea.value = sProductData;

    awProductsForm.appendChild(awProductsTextArea);

    // const userSource = getUserSource();
    const dealInfoForParts = {};
    const productsList = [];
    let totalOrderAmount = 0;
    const promoCode = setPromocode();

    const currencyCode = CURRENCY_CONFIG.code.toUpperCase();

    products.forEach((product) => {
      const categoryName = getProductCategoryName(product.dealId);
      const commissionGroup = getProductCommissionGroup(categoryName);
      const discountAmount = product.promoCodeDiscountAmount
        ? product.promoCodeDiscountAmount
        : 0;
      const productCost = product.price * product.quantity - discountAmount;
      // AW:P|awinId|orderReference|productId|productName|productItemPrice|productQuantity|productSku|commissionGroupCode|productCategory
      const productItem = [
        'AW:P',
        AWIN_ADVERTISER_ID,
        orderId,
        product.id,
        product.title,
        product.price,
        product.quantity,
        '',
        commissionGroup,
        categoryName,
      ].join('|');

      if (!dealInfoForParts[product.dealId]) {
        dealInfoForParts[product.dealId] = {
          commissionGroup,
          totalCost: 0,
        };
      }

      dealInfoForParts[product.dealId].totalCost += productCost;
      productsList.push(productItem);
      totalOrderAmount += productCost;
    });

    // add the fallback pixel
    const awinPixel = new Image(0, 0);
    awinPixel.src = `https://www.awin1.com/sread.img?tt=ns&tv=2&merchant=${AWIN_ADVERTISER_ID}&amount=${fixPrecision(
      totalOrderAmount,
    )}&ch=${AWINCHANNEL}&parts=${getParts(
      dealInfoForParts,
    )}&ref=${orderId}&vc=${promoCode}&cr=${currencyCode}&testmode=0`;
    awinPixel.className = 'awin-pixel';
    document.body.appendChild(awinPixel);

    window.AWIN.Tracking.Sale = {};
    window.AWIN.Tracking.Sale.channel = AWINCHANNEL;
    window.AWIN.Tracking.Sale.currency = currencyCode;
    window.AWIN.Tracking.Sale.orderRef = orderId;
    window.AWIN.Tracking.Sale.voucher = promoCode;
    window.AWIN.Tracking.Sale.test = 0;
    window.AWIN.Tracking.Sale.amount = fixPrecision(totalOrderAmount);
    window.AWIN.Tracking.Sale.parts = getParts(dealInfoForParts);
    window.AWIN.Tracking.Sale.plt = productsList;
    window.AWIN.Tracking.run();
  } catch (error) {
    ErrorTrackingService.logError(error);
  }

  return null;
};
